import { getPreviewVerticalGap, getWorkflowVerticalGap } from "@/lib/workflow/layoutPolicy";
import type { Edge, Node } from "@xyflow/react";
import { timer, type Timer } from "d3-timer";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { layoutNodes } from "../_hooks/useLayout";

export type NodesSetter = Dispatch<SetStateAction<Node[]>>;

type NodePosition = { x: number; y: number };
type FitViewFn = (options?: { duration?: number; padding?: number; minZoom?: number; maxZoom?: number }) => void;

interface LayoutTransition {
  from: NodePosition;
  node: Node;
  to: NodePosition;
}

interface WorkflowLayoutPlan {
  finalNodes: Node[];
  hasChanges: boolean;
  transitions: LayoutTransition[];
}

interface CreateWorkflowLayoutPlanOptions {
  edges: Edge[];
  getNode?: (id: string) => Node | undefined;
  isCompact: boolean;
  nodes: Node[];
  preview?: boolean;
  useFreshMeasurements?: boolean;
}

interface RunWorkflowLayoutTransitionOptions {
  fitView?: FitViewFn;
  fitViewOptions?: { duration?: number; padding?: number; minZoom?: number; maxZoom?: number };
  fitViewWhenUnchanged?: boolean;
  getEdges: () => Edge[];
  getNode?: (id: string) => Node | undefined;
  getNodes: () => Node[];
  isCompact: boolean;
  easing?: (progress: number) => number;
  persistNodes?: NodesSetter;
  persistNodesImmediately?: boolean;
  preview?: boolean;
  setNodes: NodesSetter;
  timerRef?: MutableRefObject<Timer | null>;
  useFreshMeasurements?: boolean;
  animationDuration?: number;
}

export function readRenderedNodeMeasurements() {
  if (typeof document === "undefined") {
    return new Map<string, { width: number; height: number }>();
  }

  const measuredMap = new Map<string, { width: number; height: number }>();
  document.querySelectorAll<HTMLElement>(".react-flow__node[data-id]").forEach(element => {
    const nodeId = element.dataset.id;
    if (!nodeId) return;
    measuredMap.set(nodeId, { width: element.offsetWidth, height: element.offsetHeight });
  });

  return measuredMap;
}

export function areRenderedMeasurementsEqual(
  a: Map<string, { width: number; height: number }>,
  b: Map<string, { width: number; height: number }>,
) {
  if (a.size !== b.size) {
    return false;
  }

  for (const [nodeId, dims] of a) {
    const other = b.get(nodeId);
    if (!other || other.width !== dims.width || other.height !== dims.height) {
      return false;
    }
  }

  return true;
}

export function withRenderedNodeMeasurements(nodes: Node[]): Node[] {
  const measuredMap = readRenderedNodeMeasurements();

  return nodes.map(node => ({
    ...node,
    measured: measuredMap.get(node.id) ?? node.measured,
  }));
}

function getLayoutVerticalGap(isCompact: boolean, preview = false) {
  return preview ? getPreviewVerticalGap(isCompact) : getWorkflowVerticalGap(isCompact);
}

export function createWorkflowLayoutPlan({
  edges,
  getNode,
  isCompact,
  nodes,
  preview = false,
  useFreshMeasurements = false,
}: CreateWorkflowLayoutPlanOptions): WorkflowLayoutPlan | null {
  if (nodes.length === 0) {
    return null;
  }

  const nodesForLayout = useFreshMeasurements ? withRenderedNodeMeasurements(nodes) : nodes;
  const measuredMap = new Map(nodesForLayout.map(node => [node.id, node.measured]));
  const targetNodes = layoutNodes(nodesForLayout, edges, {
    verticalGap: getLayoutVerticalGap(isCompact, preview),
  });
  if (targetNodes.length === 0) {
    return null;
  }

  const currentNodeMap = new Map(nodes.map(node => [node.id, node]));
  const transitions = targetNodes.map(node => {
    const currentNode = currentNodeMap.get(node.id);
    return {
      from: getNode?.(node.id)?.position ?? currentNode?.position ?? node.position,
      node: { ...node, measured: measuredMap.get(node.id) ?? node.measured },
      to: node.position,
    };
  });

  const hasChanges = targetNodes.some(node => {
    const currentNode = currentNodeMap.get(node.id);
    if (!currentNode) {
      return true;
    }

    return (
      Math.abs(currentNode.position.x - node.position.x) > 0.5 ||
      Math.abs(currentNode.position.y - node.position.y) > 0.5 ||
      currentNode.hidden !== node.hidden
    );
  });

  return {
    finalNodes: transitions.map(({ node }) => node),
    hasChanges,
    transitions,
  };
}

export function runWorkflowLayoutTransition({
  fitView,
  fitViewOptions,
  fitViewWhenUnchanged = false,
  getEdges,
  getNode,
  getNodes,
  isCompact,
  easing,
  persistNodes,
  persistNodesImmediately = false,
  preview = false,
  setNodes,
  timerRef,
  useFreshMeasurements = false,
  animationDuration = 400,
}: RunWorkflowLayoutTransitionOptions): boolean {
  const plan = createWorkflowLayoutPlan({
    edges: getEdges(),
    getNode,
    isCompact,
    nodes: getNodes(),
    preview,
    useFreshMeasurements,
  });
  if (!plan) {
    return false;
  }

  timerRef?.current?.stop();

  if (!plan.hasChanges) {
    if (fitViewWhenUnchanged) {
      fitView?.(fitViewOptions);
    }
    return false;
  }

  if (persistNodesImmediately && persistNodes) {
    setNodes(plan.finalNodes);
    persistNodes(plan.finalNodes);
    fitView?.(fitViewOptions);
    return true;
  }

  const activeTimer = timer((elapsed: number) => {
    const progress = Math.min(elapsed / animationDuration, 1);
    const animatedProgress = easing ? easing(progress) : progress;

    const animatedNodes = plan.transitions.map(({ node, from, to }) => ({
      ...node,
      position: {
        x: from.x + (to.x - from.x) * animatedProgress,
        y: from.y + (to.y - from.y) * animatedProgress,
      },
    }));

    setNodes(animatedNodes);

    if (progress >= 1) {
      activeTimer.stop();
      if (timerRef) {
        timerRef.current = null;
      }
      if (persistNodes) {
        persistNodes(plan.finalNodes);
      }
      fitView?.(fitViewOptions);
    }
  });

  if (timerRef) {
    timerRef.current = activeTimer;
  }

  return true;
}
